import { useEffect, useMemo, useRef, useState } from "react";

import { count, humanise } from "../lib/format";
import type { RunEvent } from "../lib/types";
import type { StreamState } from "../lib/useEventStream";
import { Button, Empty } from "./primitives";

/** Events worth surfacing by default. The rest are noise unless asked for. */
const SIGNAL = new Set([
  "phase.start",
  "phase.end",
  "gate.verdict",
  "tool.use",
  // The answer, not only the question. A `memory_search` that found something
  // and one that found nothing were the same line until this — so "does the
  // graph know about this company?" could not be read off the run.
  "tool.result",
  "tool_result.web",
  "runtime.error",
  "citations.checked",
  // Work somebody asked for after the run ended. Without these the tab shows
  // a deck build as a run of bare tool calls under a `run finished` line —
  // which reads as the log having lost its place.
  "task.start",
  "task.end",
  "run.finished",
  "run.error",
  "run.cancelled",
]);

/** How much of a result fits on the row before the arrow has to earn its keep. */
const ROW_CHARS = 96;

/** A block of text as one readable line. */
function oneLine(text: string): string {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length <= ROW_CHARS ? flat : `${flat.slice(0, ROW_CHARS - 1)}…`;
}

/**
 * One line for one event.
 *
 * Exported because the deck-building frame shows the same events while a
 * build runs, and two formatters would be two vocabularies for one log — the
 * tab would say `→ deck_storyline` and the frame something else, for the same
 * row of the same file.
 */
export function describe(event: RunEvent): { text: string; tone: string } {
  switch (event.type) {
    case "phase.start":
      return { text: `▸ ${humanise(String(event.phase))} started`, tone: "text-ink font-medium" };
    case "phase.end":
      return {
        text: `■ ${humanise(String(event.phase))} ${event.status} · ${event.turns ?? 0} turns`,
        tone: event.status === "done" ? "text-ink font-medium" : "text-critical-text font-medium",
      };
    case "gate.verdict":
      return {
        text: `red team verdict: ${event.verdict}`,
        tone: event.verdict === "BLOCKING" ? "text-critical-text font-medium" : "text-success-text font-medium",
      };
    case "tool.use": {
      const tool = String(event.tool ?? "?").replace(/^mcp__[^_]+__/, "");
      return { text: `→ ${tool}${event.summary ? ` · ${event.summary}` : ""}`, tone: "text-ink-2" };
    }
    case "tool.result": {
      if (event.is_error) {
        return {
          text: `↳ failed${event.text ? ` — ${oneLine(String(event.text))}` : ""}`,
          tone: "text-critical-text",
        };
      }
      const chars = Number(event.chars ?? 0);
      // A tool that answered with nothing is a finding in itself, and it is
      // not the same as a tool whose answer we failed to record.
      if (!chars) return { text: "↳ no output", tone: "text-muted" };
      return {
        text: `↳ ${oneLine(String(event.text ?? ""))}`,
        tone: "text-ink-2",
      };
    }
    case "tool_result.web":
      return {
        text: `↓ retrieved ${event.urls?.length ?? 0} page(s) via ${event.via}`,
        tone: "text-ink-2",
      };
    case "task.start":
      if (event.kind === "summary") {
        return { text: "▸ writing the board summary", tone: "text-ink font-medium" };
      }
      if (event.kind === "finetune") {
        return {
          text: `▸ finetuning ${event.target ? `“${event.target}”` : "a part"}${
            event.source ? ` in ${event.source}` : ""
          }`,
          tone: "text-ink font-medium",
        };
      }
      return {
        text: `▸ building a ${event.kind === "deck" ? "deck" : String(event.kind ?? "file")}${
          event.source ? ` from ${event.source}` : ""
        }`,
        tone: "text-ink font-medium",
      };
    case "task.step":
      return { text: `· ${String(event.text ?? "")}`, tone: "text-ink-2" };
    case "task.end": {
      if (event.kind === "summary") {
        return event.error
          ? {
              text: `■ board summary — ${oneLine(String(event.error))}`,
              tone: "text-critical-text font-medium",
            }
          : {
              text: `■ board summary — wrote ${String(event.file ?? "nothing")}`,
              tone: "text-ink font-medium",
            };
      }
      if (event.kind === "finetune") {
        const changed = event.changed ?? [];
        return event.error && changed.length === 0
          ? {
              text: `■ finetune — ${oneLine(String(event.error))}`,
              tone: "text-critical-text font-medium",
            }
          : {
              text: changed.length
                ? `■ finetune — changed ${changed.join(", ")}, rendered again`
                : "■ finetune — nothing changed",
              tone: "text-ink font-medium",
            };
      }
      const wrote = event.decks ?? [];
      if (event.error) {
        return {
          text: `■ deck — ${oneLine(String(event.error))}`,
          tone: "text-critical-text font-medium",
        };
      }
      // The storyline half is the same thing the Deliverables note says, and
      // it is the part worth reading: nothing enforces that the argument was
      // checked before the slides, so whether it was is a finding.
      const checked = event.storyline
        ? `argument checked in ${event.storyline}`
        : "no storyline was checked";
      return {
        text: `■ deck — wrote ${wrote.join(", ") || "nothing"} · ${checked}`,
        tone: "text-ink font-medium",
      };
    }
    case "memory.recalled": {
      // What the first phase was handed, before it searched anything — the
      // line that answers "did this run start from what we already knew?".
      const asked = (event.asked as string[] | undefined) ?? [];
      const items = Number(event.items ?? 0);
      if (items === 0) {
        return { text: `memory — nothing on ${asked.join(" or ")}`, tone: "text-ink-2" };
      }
      const runs = Number(event.runs ?? 0);
      const from = runs ? ` from ${runs} earlier engagement${runs === 1 ? "" : "s"}` : "";
      const spelled = event.spelled_as ? ` · found as ${event.spelled_as}` : "";
      return {
        text: `memory — ${items} items handed to the first phase${from}${spelled}`,
        tone: "text-ink font-medium",
      };
    }
    case "memory.recall_failed":
      return { text: `memory could not be asked: ${event.error}`, tone: "text-critical-text" };
    case "citations.checked":
      return { text: `citation check — ${event.summary}`, tone: "text-ink-2" };
    case "runtime.error":
    case "run.error":
      return { text: `runtime error: ${event.error}`, tone: "text-critical-text" };
    case "run.cancelled":
      // A choice, not a fault: the run list and the badge say so too.
      return { text: "run cancelled", tone: "text-ink font-medium" };
    case "run.finished":
      return { text: `run finished — ${event.status}`, tone: "text-ink font-medium" };
    case "assistant.text":
      return { text: String(event.preview ?? ""), tone: "text-muted italic" };
    default:
      return { text: event.type, tone: "text-muted" };
  }
}

/**
 * One line of the log, with what a tool answered behind an arrow.
 *
 * Expanded in place rather than in a dialog: the question that sends somebody
 * here is "what did it get back", and the answer is only useful next to the
 * call that produced it.
 */
function Row({ event }: { event: RunEvent }) {
  const [open, setOpen] = useState(false);
  const { text, tone } = describe(event);

  // Worth expanding only when there is more than the row already shows.
  const body = event.type === "tool.result" ? String(event.text ?? "") : "";
  const chars = Number(event.chars ?? 0);
  const expandable = body.length > 0 && oneLine(body).endsWith("…");

  return (
    <div className="py-0.5">
      <div className="flex gap-3">
        <span className="tabular w-12 shrink-0 text-right text-muted">
          {event.elapsed_s !== undefined ? `${Math.round(event.elapsed_s)}s` : ""}
        </span>
        <span className={`min-w-0 break-words ${tone}`}>{text}</span>
        {expandable && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="shrink-0 text-muted transition-colors hover:text-ink"
            title={open ? "Hide the full result" : "Show the full result"}
          >
            {open ? "▾" : "▸"}
          </button>
        )}
      </div>
      {open && (
        <pre className="ml-[3.75rem] mt-1 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-sm border border-line bg-surface-2 p-3 text-xs leading-relaxed text-ink-2">
          {body}
          {chars > body.length && (
            <span className="mt-2 block text-muted">
              — showing the first {count(body.length)} of{" "}
              {count(chars)} characters. The whole answer is in the
              phase transcript in the run directory.
            </span>
          )}
        </pre>
      )}
    </div>
  );
}

export function EventLog({
  events,
  state,
}: {
  events: RunEvent[];
  state: StreamState;
}) {
  const [showAll, setShowAll] = useState(false);
  const [follow, setFollow] = useState(true);
  const scroller = useRef<HTMLDivElement>(null);

  const visible = useMemo(
    () => (showAll ? events : events.filter((e) => SIGNAL.has(e.type))),
    [events, showAll],
  );

  useEffect(() => {
    if (!follow || !scroller.current) return;
    scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [visible, follow]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-line px-4 py-2 text-xs">
        <span
          className={`inline-flex items-center gap-1.5 ${
            state === "open" ? "text-success-text" : "text-muted"
          }`}
        >
          <span
            className={`size-1.5 rounded-full ${
              state === "open"
                ? "animate-pulse bg-good"
                : state === "error"
                  ? "bg-critical"
                  : "bg-baseline"
            }`}
            aria-hidden
          />
          {state === "open" ? "live" : state}
        </span>
        <span className="tabular text-muted">{visible.length} events</span>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? "Key events" : "All events"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFollow((v) => !v)}
            aria-pressed={follow}
          >
            {follow ? "Following" : "Follow"}
          </Button>
        </div>
      </div>

      <div
        ref={scroller}
        onScroll={(e) => {
          const el = e.currentTarget;
          const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
          if (!atBottom && follow) setFollow(false);
        }}
        className="min-h-0 flex-1 overflow-y-auto px-4 py-2 font-mono text-xs leading-relaxed"
      >
        {visible.length === 0 ? (
          <Empty
            title="No events yet"
            hint="Activity appears here as the agent works."
          />
        ) : (
          visible.map((event, index) => (
            <Row key={index} event={event} />
          ))
        )}
      </div>
    </div>
  );
}
